/**
 * Created by Richard on 29.06.22
 */

import {isValid, format} from 'date-fns';
import {parseDateString} from './date';

/**
 * ItemExporter a PR to arrays of data
 * @param {!Object} pr
 * @param {!number} prId
 * @return {!Array<*>}
 */
export const itemsExportPR = (pr, prId) => {
    const exporter = new ItemExporter();
    const exporters = Array.from(exporter.exporters.values());
    const values = exporters.map((e) => e(pr));

    const materialsData = values[0][0];
    const financialData = values[1][0];

    return materialsData.map((materialItem, index) => {
        const financialItem = financialData[index] || ['', '', '', '', '', ''];
        const materialId = Number(pr.materials[index].id);
        const lineItemNumber = (materialId + 1) * 10;

        const sourcingEventID = pr.sourcingEventID || '';
        const contractID = pr.contractID || '';

        const material = pr.materials[index];
        const quantity = Number(material.quantity) || 0;
        const price = Number(material.price) || 0;
        const subtotal = quantity * price;

        const supplierStatus = material.supplierStatus || '';
        const l3Category = material.l3Category || '';
        const materialType = material.type || '';

        return [
            prId,
            ...materialItem,
            ...financialItem,
            lineItemNumber,
            sourcingEventID,
            contractID,
            supplierStatus,
            subtotal.toString(),
            l3Category,
            materialType,
        ];
    });
};

/**
 * Utility class to export PRs
 */
class ItemExporter {
    /**
     * Create an exporter able to export any PR
     */
    constructor() {
        /**
         * @private
         * @type {Map<!Exporter.PRField, !Array<string>>}
         */
        this._mappings = new Map();
        /**
         * @private
         * @type {Map<!Exporter.PRField, function>}
         */
        this._exporters = new Map();

        // Exportation function for each field to allow easy iteration
        this._exporters.set(ItemExporter.PRField.materials, ItemExporter.materials);
        this._exporters.set(ItemExporter.PRField.financialInfo, ItemExporter.financialInfo);
    }

    /**
     * Get the financialInfo exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!*>} The financialInfo exported value.
     */
    static financialInfo(pr) {
        const info = pr.financialInfo;
        const materialCount = pr.materials.length;
        /**
         * @param {!Account} account
         * @return {!Array<string>}
         */
        const exportAccount = (account) => {
            // Cost center -> Costcenter / Gl number
            // WBS / Project -> WBS / GL number
            // Profit center -> Profit Center / GL number
            // Statistical -> WBS / Costcenter / GL number
            const showWBS = account.accountType.label === 'WBS / Project' || account.accountType.label === 'Statistical WBS';
            const showCostCenter = account.accountType.label === 'Cost Center' || account.accountType.label === 'Statistical WBS';
            const showProfitCenter = account.accountType.label === 'Profit Center';
            const result = [
                ItemExporter.stringDefaultOrValue(showWBS ? account.wbs?.value : ''),
                ItemExporter.stringDefaultOrValueWithoutDescription(showCostCenter ? account.costCenter?.value : ''),
                ItemExporter.stringDefaultOrValueWithoutDescription(showProfitCenter ? account.profitCenter?.value : ''),
                ItemExporter.stringDefaultOrValueWithoutDescription(account.glNumber.value),
                ItemExporter.stringDefaultOrValue(account.allocationType),
                ItemExporter.stringDefaultOrValue(account.allocateFor),
            ];
            return ItemExporter.arrayWithDefaultIfEmpty(result, 1, 6);
        };


        const accountSplitByMaterial = info.areAccountSplitByMaterial;
        let accounts;
        let mergedArray;
        if (accountSplitByMaterial) {
            accounts = info.accountDetailsByMaterial.map((accountMaterial) => {
                return accountMaterial.accounts.map((account) => [...exportAccount(account)]);
            });
            mergedArray = ItemExporter.mergeMultiDimensionalArrays(accounts);
        } else {
            accounts = info.accounts.map((account) => {
                return [...exportAccount(account)];
            });
            mergedArray = ItemExporter.mergeSingleDimensionArray(accounts);
            if ( mergedArray.length < materialCount) {
                const elementToFill = ['', '', '', '', '', ''];
                mergedArray = Array(materialCount).fill(elementToFill);
            }
            // check also how many materials we have so we can duplicate the merged array
        }

        return [mergedArray];
    }
    /**
     * Get the materials exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!Array<!*>>} The materials exported value.
     */
    static materials(pr) {
        /**
         * @param {!Material} material
         * @return {!Array<!*>}
         */
        const exportMaterial = (material) => {
            const address = material.deliveryAddress;
            const materialPSLId = ItemExporter.stringDefaultOrValue(material.uniqueSourcingToolId);
            const materialId = materialPSLId !== '' ? '' : ItemExporter.stringDefaultOrValueWithoutDescription(material.name);
            const result = [
                materialPSLId,
                materialId,
                ItemExporter.stringDefaultOrValue(material.description),
                ItemExporter.stringDefaultOrValue(material.quantity),
                ItemExporter.stringDefaultOrValue(material.unit),
                ItemExporter.stringDefaultOrValue(material.price),
                ItemExporter.stringDefaultOrValue(material.per),
                ItemExporter.stringDefaultOrValue(material.currency?.value),
                ItemExporter.booleanDefaultOrValue(material.freeItem),
                ItemExporter.stringDefaultOrValue(material.taxCode?.value),
                ItemExporter.dateDefaultOrValue(material.requestedDeliveryDate),
                ItemExporter.stringDefaultOrValue(material.plant?.value),
                ItemExporter.stringDefaultOrValue(material.storageLocation?.value),
                ItemExporter.stringDefaultOrValue(address.name),
                ItemExporter.stringDefaultOrValue(address.name2),
                ItemExporter.stringDefaultOrValue(address.houseNumber),
                ItemExporter.stringDefaultOrValue(address.street),
                ItemExporter.stringDefaultOrValue(address.postalCode),
                ItemExporter.stringDefaultOrValue(address.city),
                ItemExporter.stringDefaultOrValue(address.state),
                ItemExporter.stringDefaultOrValue(address.country),
                ItemExporter.stringDefaultOrValue(material.supplierPartId),
                ItemExporter.stringDefaultOrValue(material.internalComments),
                ItemExporter.stringDefaultOrValue(material.supplierComments),
            ];

            return ItemExporter.arrayWithDefaultIfEmpty(result, 1, 24);
        };

        const materialsArray = pr.materials.map((material) => exportMaterial(material));
        return [materialsArray];
    }

    /**
     * @private
     * @param {string} value
     * @return {string} value if defined or empty string default
     */
    static stringDefaultOrValue(value) {
        return (value !== undefined && value !== null && typeof value !== 'object') ? value.toString() : '';
    }

    /**
     * @private
     * @param {string} value
     * @return {string} value if defined or empty string default
     */
    static stringDefaultOrValueWithoutDescription(value) {
        return value ? value.split('-')[0].trim() : '';
    }


    /**
     * @private
     * @param {boolean} value
     * @return {string}
     */
    static booleanDefaultOrValue(value) {
        return value !== undefined && value !== null ? (value ? 'Yes' : 'No') : 'No';
    }

    /**
     * @private
     * @param {?Date | string} value
     * @return {string}
     */
    static getReadableDate(value) {
        const readableDateOptions = {month: 'short', day: 'numeric', year: 'numeric'};
        return value.toLocaleString('en-US', readableDateOptions);
    }

    /**
     * @private
     * @param {?Date | string} value
     * @return {string}
     */
    static dateDefaultOrValue(value) {
        const date = parseDateString(value);
        return date && isValid(date) ? format(date, 'dd-MMM-yy') : '';
    }

    /**
     * @private
     * @param {!Array<!*>}array
     * @param {!number} defaultRows
     * @param {!number} defaultColumns
     * @return {!Array<!*>}
     */
    static arrayWithDefaultIfEmpty(array, defaultRows, defaultColumns) {
        return array?.length === 0 ?
            new Array(defaultRows).fill('').map(() => new Array(defaultColumns).fill('')) :
            array;
    }
    /**
     * @private
     * @param {!Array<!*>}arr
     * @return {!Array<!*>}
     */
    static mergeMultiDimensionalArrays(arr) {
        return arr?.map((materialArr)=> {
            const pushArray = ['', '', '', '', '', ''];
            materialArr.forEach((financialLine)=>{
                financialLine.forEach((item, itemIndex)=>{
                    if (itemIndex === 4) {
                        pushArray[itemIndex] = item;
                    } else if (item !=='') {
                        if (pushArray[itemIndex] === '') {
                            pushArray[itemIndex] = item;
                        } else {
                            pushArray[itemIndex] += `;${item}`;
                        }
                    }
                });
            });
            return pushArray;
        });
    };

    /**
     * @private
     * @param {!Array<!*>}arr
     * @return {!Array<!*>}
     */
    static mergeSingleDimensionArray(arr) {
        const expectedLength = 6;
        const pushArray = new Array(expectedLength).fill('');
        arr.forEach((financialLine) => {
            financialLine.forEach((item, itemIndex) => {
                if (itemIndex >= expectedLength) {
                    return;
                }
                if (itemIndex === 4) {
                    pushArray[itemIndex] = item;
                } else if (item !== '') {
                    if (pushArray[itemIndex] === '') {
                        pushArray[itemIndex] = item;
                    } else {
                        pushArray[itemIndex] += `;${item}`;
                    }
                }
            });
        });
        return [pushArray];
    };
    /**
     * @private
     * @param {!Array<!*>}arr
     * @return {!Array<!*>}
     */
    static mergeMultidimensionalArrays(arr) {
        return arr?.map((materialArr)=> {
            let pushArray = ['', '', '', '', '', ''];
            materialArr.forEach((financialLine, index)=>{
                if ( Array.isArray(financialLine[0])) {
                    financialLine.forEach((item, itemIndex)=>{
                        if (itemIndex === 4) {
                            pushArray[itemIndex] = item;
                        } else if (item !=='') {
                            if (pushArray[itemIndex] === '') {
                                pushArray[itemIndex] = item;
                            } else {
                                pushArray[itemIndex] += `;${item}`;
                            }
                        }
                    });
                } else {
                    pushArray = [financialLine];
                }
            });
            return pushArray;
        });
    };

    get mappings() {
        return this._mappings;
    }

    get exporters() {
        return this._exporters;
    }
}

/**
 * @enum {number}
 */
ItemExporter.PRField = {
    materials: 0,
    financialInfo: 1,
};
